import React from 'react';
import { format } from 'date-fns';
import { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";
import { Badge } from "../../ui/badge";
import { Card } from "../../ui/card";

const ManageReservationsTable = ({ reservations, allReservations, onFilterChange }) => {
  const { t } = useTranslation();
  const [statusFilter, setStatusFilter] = useState("all");

  const getReservationStatus = (reservation) => {
    if (reservation.status === "completed") {
      return { label: t('parking.reservations.status.completed'), variant: "secondary" };
    }
    if (reservation.status === "cancelled") {
      return { label: t('parking.reservations.status.cancelled'), variant: "destructive" };
    }
    if (reservation.status === "active") {
      return { label: t('parking.reservations.status.active'), variant: "success" };
    }
    
    // Default case when status doesn't match any of the above conditions
    return { label: t('parking.reservations.status.unknown'), variant: "default" };
  };

  // Handle status filter change
  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    
    // Filter the full set of reservations based on the new status
    let filtered = [...allReservations];
    if (value !== "all") {
      filtered = filtered.filter(reservation => {
        const status = reservation.status;
        return status === value;
      });
    }
    
    // Notify parent component about filtered results
    if (onFilterChange) {
      onFilterChange(filtered);
    }
  };

  // Mobile view card renderer
  const renderMobileCard = (reservation) => {
    const status = getReservationStatus(reservation);
    
    return (
      <Card key={reservation.id} className="p-4 mb-3">
        <div className="flex justify-between items-start mb-2">
          <div className="font-medium">{reservation.user_name}</div>
          <Badge variant={status?.variant || "default"}>
            {status?.label || t('parking.reservations.status.unknown')}
          </Badge>
        </div>
        
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div className="text-muted-foreground">{t('parking.reservations.reserved')}:</div>
          <div>{format(new Date(reservation.created_at), 'dd/MM/yyyy')}</div>
          
          <div className="text-muted-foreground">{t('parking.reservations.period')}:</div>
          <div>
            {format(new Date(reservation.start_time), 'dd/MM/yyyy')} {t('parking.reservations.to')}{' '}
            {format(new Date(reservation.end_time), 'dd/MM/yyyy')}
          </div>
          
          <div className="text-muted-foreground">{t('parking.reservations.points')}:</div>
          <div className="font-semibold">{reservation.points_cost} {t('parking.reservations.pts')}</div>
        </div>
      </Card>
    );
  };

  return (
    <div>
      <div className="mb-4 flex justify-end">
        <Select value={statusFilter} onValueChange={handleStatusFilterChange}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder={t('parking.reservations.filterPlaceholder')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('parking.reservations.filters.all')}</SelectItem>
            <SelectItem value="completed">{t('parking.reservations.status.completed')}</SelectItem>
            <SelectItem value="cancelled">{t('parking.reservations.status.cancelled')}</SelectItem>
            <SelectItem value="active">{t('parking.reservations.status.active')}</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      {/* Mobile view (cards) */}
      <div className="md:hidden space-y-3">
        {reservations.length > 0 ? (
          reservations.map(renderMobileCard)
        ) : (
          <div className="text-center py-6 text-muted-foreground">
            {t('parking.reservations.noReservations')}
          </div>
        )}
      </div>
      
      {/* Desktop view (table) */}
      <div className="hidden md:block rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('parking.reservations.table.reservedBy')}</TableHead>
              <TableHead>{t('parking.reservations.table.reservedAt')}</TableHead>
              <TableHead>{t('parking.reservations.table.dates')}</TableHead>
              <TableHead>{t('parking.reservations.table.pointsEarned')}</TableHead>
              <TableHead>{t('parking.reservations.table.status')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {reservations.map((reservation) => {
              const status = getReservationStatus(reservation);
              return (
                <TableRow key={reservation.id}>
                  <TableCell className="font-medium">
                    {reservation.user_name}
                  </TableCell>
                  <TableCell className="text-sm">
                    {format(new Date(reservation.created_at), 'dd/MM/yyyy')}
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      {format(new Date(reservation.start_time), 'dd/MM/yyyy')}
                    </div>
                    <div className="text-sm text-gray-500">
                      {t('parking.reservations.to')} {format(new Date(reservation.end_time), 'dd/MM/yyyy')}
                    </div>
                  </TableCell>
                  <TableCell>{reservation.points_cost} {t('parking.reservations.pts')}</TableCell>
                  <TableCell>
                    <Badge variant={status?.variant || "default"}>
                      {status?.label || t('parking.reservations.status.unknown')}
                    </Badge>
                  </TableCell>
                </TableRow>
              );
            })}
            {reservations.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-4 text-gray-500">
                  {t('parking.reservations.noReservations')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default ManageReservationsTable;